import React, { ReactElement, useEffect, useState, useRef } from "react";
import { PoserApp, PoserCallbacks } from "../poser";
import { FieldVisionController } from "./FieldVisionController";
import PlaybackControls from "./components/PlaybackControls/PlaybackControls";
import LoadingProgressIndicator from "./components/LoadingProgressIndicator/LoadingProgressIndicator";
import { Context, PoserOptions } from "../context";
import noop from "lodash/noop";

interface ComponentSpecificOptions {
  loadingProcess?: boolean;
  callbacks?: {
    onFullscreen?(enteringFullscreen: boolean): void;
    onInfoButton?(): void;
  };
}

interface FieldVisionPlayerProps {
  /**
   * @deprecated parent container ref is no longer used
   */
  containerRef?: React.RefObject<HTMLElement>;
  controllerRef: React.MutableRefObject<FieldVisionController | null>;
  options: PoserOptions & ComponentSpecificOptions;
}

function FieldVisionPlayer(
  props: FieldVisionPlayerProps
): ReactElement<FieldVisionPlayerProps> | null {
  const [initialized, setInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const showControls = () => {
    let controls = document.getElementById("fvodControls");
    if (controls) {
      controls.hidden = false;
    }
  };

  // Default value to true
  const showLoadingProgress =
    props.options.loadingProcess === undefined || props.options.loadingProcess;

  useEffect(() => {
    const initializePoser = async () => {
      if (containerRef.current && !initialized) {
        const callbacks: Partial<PoserCallbacks> = {};

        if (props.options.callbacks) {
          if (props.options.callbacks.onGumboChange !== undefined) {
            callbacks.onGumboChange = props.options.callbacks.onGumboChange;
          }
          callbacks.onCanPlay = () => {
            showControls();
            if (props.options.callbacks?.onCanPlay !== undefined) {
              props.options.callbacks.onCanPlay();
            }
          };
          if (props.options.callbacks.onCameraChange !== undefined) {
            callbacks.onCameraChange = props.options.callbacks.onCameraChange;
          }
          if (props.options.callbacks.onRenderLoopTick !== undefined) {
            callbacks.onRenderLoopTick =
              props.options.callbacks.onRenderLoopTick;
          }
        } else {
          callbacks.onCanPlay = showControls;
        }

        const app = new PoserApp({
          ...props.options,
          callbacks,
        });
        const fieldVisionController = await app.init(containerRef.current);
        if (fieldVisionController) {
          props.controllerRef.current = fieldVisionController;
        }
        setInitialized(true);
      }
    };
    initializePoser();
  }, [containerRef, initialized]);

  useEffect(() => {
    if (props.controllerRef.current) {
      props.controllerRef.current.setBearerToken(props.options.bearerToken);
    }
  }, [props.options.bearerToken, props.controllerRef]);

  // empty array parameter ensures this `useEffect` is only run once.
  useEffect(() => {
    return function cleanup() {
      props.controllerRef.current?.cleanUp();
      props.controllerRef.current = null;
    };
  }, []);

  const controller = props.controllerRef.current;
  const controls =
    containerRef.current && props.options.embeddedControls && controller ? (
      <>
        {showLoadingProgress ? (
          <LoadingProgressIndicator
            addSubscriberToLoadingStatus={(
              name: string,
              cb: (pctLoaded: number) => void
            ) => controller.addSubscriberToLoadingStatus(name, cb)}
          />
        ) : null}
        <PlaybackControls
          containerRef={containerRef}
          addSubscriberToContextChange={(
            name: string,
            cb: (context: Context) => void
          ) => controller.addSubscriberToContextChange(name, cb)}
          isPlaying={controller.isPlaying}
          play={() => {
            controller.play();
          }}
          pause={() => {
            controller.pause();
          }}
          rewind={() => {
            controller.playCurrentTime = controller.playCurrentTime - 10;
          }}
          fastForward={() => {
            controller.playCurrentTime = controller.playCurrentTime + 10;
          }}
          onFullscreen={props.options.callbacks?.onFullscreen || noop}
          onInfoButton={props.options.callbacks?.onInfoButton || noop}
          toggleLoop={() => {
            controller.toggleLoop();
          }}
          controller={controller}
        />
      </>
    ) : null;
  return (
    <div
      ref={containerRef}
      style={{
        margin: 0,
        padding: 0,
        width: "100%",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {controls}
    </div>
  );
}

// This is ultimately the thing we'll want to publish for consumption by Gameday.
export default FieldVisionPlayer;
